import {
  DASHBOARD_METRIC_STATUS,
  DASHBOARD_VISUALIZATION_TYPE,
  DASHBOARD_Y_AXIS
} from './dashboardContract';

export const DASHBOARD_VISUALIZATION_TYPE_OPTIONS = Object.freeze([
  { value: DASHBOARD_VISUALIZATION_TYPE.LINE, label: 'Line graph' },
  { value: DASHBOARD_VISUALIZATION_TYPE.BAR, label: 'Bar graph' }
]);

export const DASHBOARD_Y_AXIS_OPTIONS = Object.freeze([
  { value: DASHBOARD_Y_AXIS.LEFT, label: 'Left axis' },
  { value: DASHBOARD_Y_AXIS.RIGHT, label: 'Right axis' }
]);

export const DASHBOARD_VISUALIZATION_MAX_SERIES = 6;

export const EMPTY_DASHBOARD_VISUALIZATION_FORM = Object.freeze({
  name: '',
  description: '',
  visualizationType: DASHBOARD_VISUALIZATION_TYPE.LINE,
  xAxisLabel: '',
  leftYAxisLabel: '',
  rightYAxisLabel: '',
  showLegend: true,
  series: []
});

const visualizationTypeValues = DASHBOARD_VISUALIZATION_TYPE_OPTIONS.map(
  ({ value }) => value
);
const yAxisValues = DASHBOARD_Y_AXIS_OPTIONS.map(({ value }) => value);

export const sortDashboardVisualizations = (visualizations = []) =>
  [...visualizations].sort(
    (left, right) =>
      left.displayOrder - right.displayOrder ||
      left.name.localeCompare(right.name)
  );

export const sortDashboardVisualizationSeries = (series = []) =>
  [...series].sort((left, right) => left.seriesOrder - right.seriesOrder);

export const dashboardVisualizationToForm = (visualization) => ({
  name: visualization?.name ?? '',
  description: visualization?.description ?? '',
  visualizationType:
    visualization?.visualizationType ?? DASHBOARD_VISUALIZATION_TYPE.LINE,
  xAxisLabel: visualization?.xAxisLabel ?? '',
  leftYAxisLabel: visualization?.leftYAxisLabel ?? '',
  rightYAxisLabel: visualization?.rightYAxisLabel ?? '',
  showLegend: visualization?.showLegend ?? true,
  series: sortDashboardVisualizationSeries(visualization?.series).map(
    (series) => ({
      metricDefinitionId: series.metricDefinitionId,
      yAxis: series.yAxis,
      colour: series.colour ?? ''
    })
  )
});

export const toggleDashboardVisualizationMetric = (
  series,
  metricDefinitionId,
  selected
) => {
  const existing = series.some(
    (item) => item.metricDefinitionId === metricDefinitionId
  );
  if (selected && !existing) {
    if (series.length >= DASHBOARD_VISUALIZATION_MAX_SERIES) return series;
    return [
      ...series,
      {
        metricDefinitionId,
        yAxis: DASHBOARD_Y_AXIS.LEFT,
        colour: ''
      }
    ];
  }
  if (!selected && existing) {
    return series.filter(
      (item) => item.metricDefinitionId !== metricDefinitionId
    );
  }
  return series;
};

export const moveDashboardVisualizationSeries = (
  series,
  metricDefinitionId,
  direction
) => {
  const ordered = [...series];
  const currentIndex = ordered.findIndex(
    (item) => item.metricDefinitionId === metricDefinitionId
  );
  const targetIndex = currentIndex + direction;
  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) {
    return ordered;
  }
  const [item] = ordered.splice(currentIndex, 1);
  ordered.splice(targetIndex, 0, item);
  return ordered;
};

export const moveDashboardVisualization = (
  visualizations,
  dashboardVisualizationId,
  direction
) => {
  const ordered = sortDashboardVisualizations(visualizations);
  const currentIndex = ordered.findIndex(
    (item) => item.dashboardVisualizationId === dashboardVisualizationId
  );
  const targetIndex = currentIndex + direction;
  if (currentIndex < 0 || targetIndex < 0 || targetIndex >= ordered.length) {
    return ordered.map((item) => item.dashboardVisualizationId);
  }
  const [item] = ordered.splice(currentIndex, 1);
  ordered.splice(targetIndex, 0, item);
  return ordered.map((visualization) => visualization.dashboardVisualizationId);
};

const normalizedText = (value) =>
  typeof value === 'string' ? value.trim() : '';

export const validateDashboardVisualization = (form, activeMetrics = []) => {
  const errors = {};
  const name = normalizedText(form?.name);
  const description = normalizedText(form?.description);
  const xAxisLabel = normalizedText(form?.xAxisLabel);
  const leftYAxisLabel = normalizedText(form?.leftYAxisLabel);
  const rightYAxisLabel = normalizedText(form?.rightYAxisLabel);
  const activeMetricIds = new Set(
    activeMetrics
      .filter(
        (metric) => metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ACTIVE
      )
      .map((metric) => metric.metricDefinitionId)
  );

  if (!name) {
    errors.name = 'Graph name is required.';
  } else if (name.length > 100) {
    errors.name = 'Graph name cannot exceed 100 characters.';
  }
  if (description.length > 500) {
    errors.description = 'Description cannot exceed 500 characters.';
  }
  [
    ['xAxisLabel', xAxisLabel, 'X-axis label'],
    ['leftYAxisLabel', leftYAxisLabel, 'Left y-axis label'],
    ['rightYAxisLabel', rightYAxisLabel, 'Right y-axis label']
  ].forEach(([field, value, label]) => {
    if (value.length > 100) {
      errors[field] = `${label} cannot exceed 100 characters.`;
    }
  });
  if (!visualizationTypeValues.includes(form?.visualizationType)) {
    errors.visualizationType = 'Select a supported graph type.';
  }

  const rawSeries = Array.isArray(form?.series) ? form.series : [];
  if (!rawSeries.length) {
    errors.series = 'Select at least one active metric for this graph.';
  } else if (rawSeries.length > DASHBOARD_VISUALIZATION_MAX_SERIES) {
    errors.series = `Select no more than ${DASHBOARD_VISUALIZATION_MAX_SERIES} metrics for one graph.`;
  }
  const ids = rawSeries.map((item) => item.metricDefinitionId);
  if (new Set(ids).size !== ids.length) {
    errors.series = 'A metric can appear only once in a graph.';
  }

  const series = rawSeries.map((item, index) => {
    const colour = normalizedText(item.colour);
    if (!activeMetricIds.has(item.metricDefinitionId)) {
      errors.series = 'Every graph series must use an active dashboard metric.';
    }
    if (!yAxisValues.includes(item.yAxis)) {
      errors[`series.${index}.yAxis`] = 'Select a supported y-axis.';
    }
    if (colour && !/^#[0-9A-Fa-f]{6}$/.test(colour)) {
      errors[`series.${index}.colour`] =
        'Colour must use a six-digit hex value such as #11A5A4.';
    }
    return {
      metricDefinitionId: item.metricDefinitionId,
      yAxis: item.yAxis,
      colour: colour ? colour.toUpperCase() : null
    };
  });

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    values: {
      configuration: {
        name,
        description: description || null,
        visualizationType: form?.visualizationType,
        xAxisLabel: xAxisLabel || null,
        leftYAxisLabel: leftYAxisLabel || null,
        rightYAxisLabel: rightYAxisLabel || null,
        showLegend: Boolean(form?.showLegend)
      },
      series
    }
  };
};

export const getActiveDashboardMetrics = (metrics = []) =>
  [...metrics]
    .filter(
      (metric) => metric.lifecycleStatus === DASHBOARD_METRIC_STATUS.ACTIVE
    )
    .sort(
      (left, right) =>
        left.displayOrder - right.displayOrder ||
        left.label.localeCompare(right.label)
    );
